import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { API } from "../App";
import { TYPEFORM_QUIZ_URL } from "../lib/externalLinks";
import { NEIGHBORHOODS, HOMEPAGE_NEIGHBORHOODS } from "../lib/neighborhoodConfig";
import { latestCornerstoneArticles } from "../lib/blogConfig";
import {
  Search,
  MapPin,
  Sparkles,
  ArrowRight,
  CheckCircle,
  Zap,
  DollarSign,
  Calendar,
  Phone,
  FileText,
  ClipboardCheck,
  Eye,
  Heart,
  ChevronRight,
  Download,
  Shield,
  MessageCircle,
  Smartphone,
  ClipboardList
} from "lucide-react";
import FeaturedDeals from "../components/FeaturedDeals";
import { useToast } from "@/hooks/use-toast";
import { track } from "../lib/analytics";
import { getAttribution } from "../lib/attribution";
import { BRAND } from "../lib/brandConfig";

// Progressive Apartment Search Component - Two Step Flow
const ProgressiveSearch = ({ navigate, toast }) => {
  const [step, setStep] = useState(1);
  const [selectedArea, setSelectedArea] = useState("");
  const [searching, setSearching] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    budget_min: "",
    budget_max: "",
    bedrooms: "",
    move_in_date: ""
  });

  const areas = [
    { name: "Houston", icon: "🏙️", desc: "Urban living & Downtown" },
    { name: "Katy", icon: "🏡", desc: "Family-friendly suburbs" },
    { name: "Sugar Land", icon: "🌳", desc: "Upscale community" },
    { name: "The Woodlands", icon: "🌲", desc: "Nature & luxury" },
    { name: "Conroe", icon: "🚤", desc: "Lakeside living" }
  ];

  const bedroomOptions = ["Studio", "1 Bed", "2 Bed", "3+ Bed"];

  const handleAreaSelect = (area) => {
    setSelectedArea(area);
    setStep(2);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.budget_min || !formData.budget_max || !formData.bedrooms) {
      toast({
        title: "Missing Information",
        description: "Please fill in all required fields.",
        variant: "destructive"
      });
      return;
    }

    setSearching(true);
    try {
      const response = await axios.post(`${API}/instant-search`, {
        name: formData.name,
        email: formData.email,
        phone: formData.phone || null,
        budget_min: parseInt(formData.budget_min),
        budget_max: parseInt(formData.budget_max),
        bedrooms: formData.bedrooms,
        move_in_date: formData.move_in_date || null,
        preferred_area: selectedArea
      });

      navigate("/instant-results", {
        state: {
          apartments: response.data.apartments,
          searchCriteria: {
            budget_min: parseInt(formData.budget_min),
            budget_max: parseInt(formData.budget_max),
            bedrooms: formData.bedrooms,
            area: selectedArea
          },
          leadId: response.data.lead_id,
          emailSent: response.data.email_sent
        }
      });
    } catch {
      toast({
        title: "Search Failed",
        description: "Something went wrong. Please try again.",
        variant: "destructive"
      });
    }
    setSearching(false);
  };

  return (
    <section className="py-20 bg-gradient-to-b from-[#0A2342] to-[#0A2342]/95" id="instant-search">
      <div className="container-main">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <span className="inline-flex items-center px-4 py-2 bg-[#DE6E4B] rounded-full text-white text-sm font-medium mb-4">
            <Zap className="w-4 h-4 mr-2" />
            Instant Apartment Finder
          </span>
          <h2 className="text-3xl md:text-5xl font-bold text-white mb-4 font-['Playfair_Display']">
            Find Your Perfect Houston Home
          </h2>
          <p className="text-slate-300 max-w-2xl mx-auto text-lg">
            Get instant apartment matches + a follow-up email with exclusive specials
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          className="max-w-4xl mx-auto"
        >
          <div className="bg-white rounded-3xl p-8 md:p-10 shadow-2xl" data-testid="progressive-search">
            <AnimatePresence mode="wait">
              {step === 1 ? (
                <motion.div
                  key="step1"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20 }}
                >
                  <div className="text-center mb-8">
                    <h3 className="text-2xl font-bold text-[#0A2342] mb-2 font-['Playfair_Display']">
                      Where do you want to live?
                    </h3>
                    <p className="text-slate-500">Select your preferred Houston area</p>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
                    {areas.map((area) => (
                      <motion.button
                        key={area.name}
                        whileHover={{ scale: 1.03 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={() => handleAreaSelect(area.name)}
                        className="p-6 rounded-2xl border-2 border-slate-200 hover:border-[#DE6E4B] hover:bg-[#DE6E4B]/5 transition-all text-center group"
                        data-testid={`area-select-${area.name.toLowerCase().replace(/\s+/g, '-')}`}
                      >
                        <span className="text-4xl mb-3 block">{area.icon}</span>
                        <span className="font-semibold text-[#0A2342] block mb-1 group-hover:text-[#DE6E4B] transition-colors">
                          {area.name}
                        </span>
                        <span className="text-xs text-slate-400">{area.desc}</span>
                      </motion.button>
                    ))}
                  </div>

                  <div className="mt-8 text-center">
                    <button
                      onClick={() => handleAreaSelect("Houston")}
                      className="text-[#DE6E4B] font-medium hover:underline flex items-center justify-center mx-auto"
                    >
                      Not sure? Start with Houston <ArrowRight className="w-4 h-4 ml-1" />
                    </button>
                  </div>
                </motion.div>
              ) : (
                <motion.div
                  key="step2"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                >
                  <div className="flex items-center justify-between mb-6">
                    <button
                      onClick={() => setStep(1)}
                      className="text-slate-500 hover:text-[#0A2342] flex items-center text-sm"
                    >
                      ← Change Area
                    </button>
                    <span className="inline-flex items-center px-3 py-1 bg-[#8A9A5B]/20 text-[#8A9A5B] rounded-full text-sm font-medium">
                      <MapPin className="w-4 h-4 mr-1" /> {selectedArea}
                    </span>
                  </div>

                  <h3 className="text-2xl font-bold text-[#0A2342] mb-6 font-['Playfair_Display'] text-center">
                    Tell us what you're looking for
                  </h3>

                  <form onSubmit={handleSubmit} className="space-y-6">
                    {/* Budget & Bedrooms Row */}
                    <div className="grid md:grid-cols-3 gap-4">
                      <div>
                        <label className="text-sm font-medium text-slate-700 mb-2 block">
                          Min Budget *
                        </label>
                        <div className="relative">
                          <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                          <input
                            type="number"
                            required
                            value={formData.budget_min}
                            onChange={(e) => setFormData({ ...formData, budget_min: e.target.value })}
                            className="input-field pl-10"
                            placeholder="1000"
                            data-testid="prog-budget-min"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-slate-700 mb-2 block">
                          Max Budget *
                        </label>
                        <div className="relative">
                          <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                          <input
                            type="number"
                            required
                            value={formData.budget_max}
                            onChange={(e) => setFormData({ ...formData, budget_max: e.target.value })}
                            className="input-field pl-10"
                            placeholder="2000"
                            data-testid="prog-budget-max"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-slate-700 mb-2 block">
                          Bedrooms *
                        </label>
                        <select
                          required
                          value={formData.bedrooms}
                          onChange={(e) => setFormData({ ...formData, bedrooms: e.target.value })}
                          className="input-field"
                          data-testid="prog-bedrooms"
                        >
                          <option value="">Select...</option>
                          {bedroomOptions.map((opt) => (
                            <option key={opt} value={opt}>{opt}</option>
                          ))}
                        </select>
                      </div>
                    </div>

                    {/* Move-in Date */}
                    <div>
                      <label className="text-sm font-medium text-slate-700 mb-2 block">
                        Move-in Date (Optional)
                      </label>
                      <div className="relative max-w-xs">
                        <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                        <input
                          type="date"
                          value={formData.move_in_date}
                          onChange={(e) => setFormData({ ...formData, move_in_date: e.target.value })}
                          className="input-field pl-10"
                          data-testid="prog-move-date"
                        />
                      </div>
                    </div>

                    {/* Contact Info */}
                    <div className="border-t border-slate-200 pt-6">
                      <p className="text-sm text-slate-500 mb-4">
                        Where should we send your apartment matches?
                      </p>
                      <div className="grid md:grid-cols-3 gap-4">
                        <div>
                          <label className="text-sm font-medium text-slate-700 mb-2 block">Name *</label>
                          <input
                            type="text"
                            required
                            value={formData.name}
                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                            className="input-field"
                            placeholder="Your name"
                            data-testid="prog-name"
                          />
                        </div>
                        <div>
                          <label className="text-sm font-medium text-slate-700 mb-2 block">Email *</label>
                          <input
                            type="email"
                            required
                            value={formData.email}
                            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                            className="input-field"
                            placeholder="you@email.com"
                            data-testid="prog-email"
                          />
                        </div>
                        <div>
                          <label className="text-sm font-medium text-slate-700 mb-2 block">Phone</label>
                          <input
                            type="tel"
                            value={formData.phone}
                            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                            className="input-field"
                            placeholder="(555) 123-4567"
                            data-testid="prog-phone"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Submit */}
                    <button
                      type="submit"
                      disabled={searching}
                      className="w-full bg-[#DE6E4B] hover:bg-[#0A2342] text-white font-semibold py-4 px-8 rounded-xl transition-all duration-300 flex items-center justify-center text-lg"
                      data-testid="prog-submit"
                    >
                      {searching ? (
                        <>
                          <span className="animate-spin rounded-full h-6 w-6 border-b-2 border-white mr-3" />
                          Finding Apartments...
                        </>
                      ) : (
                        <>
                          <Zap className="w-6 h-6 mr-2" />
                          Show My Matches
                        </>
                      )}
                    </button>

                    <p className="text-center text-slate-400 text-sm">
                      <Shield className="w-4 h-4 inline mr-1" />
                      Your info is safe. We'll email your matches + exclusive specials.
                    </p>
                  </form>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// Checklist Lead Magnet Component
const ChecklistSection = ({ toast }) => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // PDF URLs
  const resources = {
    checklist: "https://customer-assets.emergentagent.com/job_urban-quest-30/artifacts/4vhg8h6d_Sanctuary_Seekers_Checklist.pdf",
    tourChecklist: "https://customer-assets.emergentagent.com/job_urban-quest-30/artifacts/gqsy8773_Apartment_Tour_Checklist.pdf",
    completeGuide: "https://customer-assets.emergentagent.com/job_urban-quest-30/artifacts/159sxlhc_Sanctuary_Seekers_Complete_Guide.pdf"
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || loading) return;  // prevent duplicate submit during pending state

    setLoading(true);
    const attribution = getAttribution();
    track("checklist_form_started", { email_provided: true });
    try {
      await axios.post(`${API}/leads`, {
        name: "Checklist Download",
        email: email,
        lead_type: "renter",
        source: "checklist_download",
        message: "Downloaded Sanctuary Seeker's Checklist Bundle",
        ...attribution,
      });

      // Trigger branded email with resource links
      await axios.post(`${API}/send-resources`, { email, name: "Checklist Download" });

      track("checklist_form_completed", { source: "checklist_download" });
      track("lead_created", { source: "checklist_download" });
      setSubmitted(true);
      toast({
        title: "Resources on their way!",
        description: "Check your inbox + download below.",
      });
    } catch (error) {
      const status = error?.response?.status;
      const detail = error?.response?.data?.detail;
      // Rate-limited — surface clearly, do NOT show downloads (user hit limit)
      if (status === 429) {
        const retry = detail?.retry_after_seconds || 900;
        const mins = Math.ceil(retry / 60);
        toast({
          title: "Too many submissions",
          description: `Please try again in ~${mins} min, or text us at (936) 224-1484.`,
          variant: "destructive",
        });
        track("checklist_form_blocked", { reason: "rate_limit" });
      } else {
        // Real backend failure: surface honestly but still reveal the downloads
        // (the PDFs are public — withholding them helps nobody).
        console.error("Checklist submit failed:", error);
        toast({
          title: "We had trouble emailing you",
          description: "Your downloads are below. Please screenshot or save them now.",
          variant: "destructive",
        });
        setSubmitted(true);
        track("checklist_form_completed", { source: "checklist_download", email_failed: true });
      }
    }
    setLoading(false);
  };

  const handlePdfClick = (pdfId) => {
    track("pdf_download_clicked", { pdf_id: pdfId, email: email || undefined });
    // Fire-and-forget backend tracking (non-blocking, never throws)
    try {
      axios.post(`${API}/track/pdf-click`, {
        pdf_id: pdfId,
        email: email || undefined,
        source_page: "homepage_checklist",
      }).catch(() => {});
    } catch {}
  };

  const checklistFeatures = [
    { icon: FileText, title: "The Foundation", desc: "Budget & document checklist to get you rental-ready" },
    { icon: Search, title: "The Search", desc: "Neighborhood scoring matrix & 10 must-ask questions" },
    { icon: Eye, title: "The Inspection", desc: "Room-by-room walkthrough for professional-level reviews" },
    { icon: Heart, title: "The Sanctuary", desc: "Move-in essentials & wellness setup guide" }
  ];

  return (
    <section className="py-20 bg-[#E8E8E8]" id="checklist">
      <div className="container-main">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left - Content */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
          >
            <span className="inline-flex items-center px-4 py-2 bg-[#8A9A5B] rounded-full text-white text-sm font-medium mb-4">
              <Download className="w-4 h-4 mr-2" />
              Free Downloads
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-[#0A2342] mb-4 font-['Playfair_Display']">
              The Complete Renter's Toolkit
            </h2>
            <p className="text-slate-600 text-lg mb-8">
              Get my free <strong>Sanctuary Seeker's Bundle</strong> — 4 printable resources I give all my clients to find their perfect apartment.
            </p>

            <div className="grid sm:grid-cols-2 gap-4 mb-8">
              {checklistFeatures.map((feature) => (
                <div key={feature.title} className="flex items-start space-x-3">
                  <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center flex-shrink-0">
                    <feature.icon className="w-5 h-5 text-[#DE6E4B]" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-[#0A2342]">{feature.title}</h4>
                    <p className="text-sm text-slate-500">{feature.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Right - Form or Downloads */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            className="bg-white rounded-2xl p-8 shadow-xl"
            data-testid="checklist-form"
          >
            {submitted ? (
              <div className="space-y-6">
                <div className="text-center mb-6">
                  <div className="w-16 h-16 bg-[#8A9A5B]/20 rounded-full flex items-center justify-center mx-auto mb-4">
                    <CheckCircle className="w-8 h-8 text-[#8A9A5B]" />
                  </div>
                  <h3 className="text-xl font-bold text-[#0A2342] mb-2">Your Resources Are Ready!</h3>
                  <p className="text-slate-600 text-sm">Click below to download each guide</p>
                </div>
                
                {/* Download Links */}
                <div className="space-y-3">
                  <a
                    href={resources.checklist}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => handlePdfClick("checklist")}
                    className="flex items-center justify-between p-4 bg-[#0A2342] text-white rounded-xl hover:bg-[#DE6E4B] transition-colors group"
                    data-testid="download-checklist"
                  >
                    <div className="flex items-center">
                      <FileText className="w-5 h-5 mr-3" />
                      <div>
                        <p className="font-semibold">Sanctuary Seeker's Checklist</p>
                        <p className="text-xs opacity-80">Complete renter's guide</p>
                      </div>
                    </div>
                    <Download className="w-5 h-5 group-hover:animate-bounce" />
                  </a>
                  
                  <a
                    href={resources.tourChecklist}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => handlePdfClick("tour_checklist")}
                    className="flex items-center justify-between p-4 bg-[#8A9A5B] text-white rounded-xl hover:bg-[#DE6E4B] transition-colors group"
                    data-testid="download-tour-checklist"
                  >
                    <div className="flex items-center">
                      <ClipboardCheck className="w-5 h-5 mr-3" />
                      <div>
                        <p className="font-semibold">Apartment Tour Checklist</p>
                        <p className="text-xs opacity-80">Printable inspection sheet</p>
                      </div>
                    </div>
                    <Download className="w-5 h-5 group-hover:animate-bounce" />
                  </a>
                  
                  <a
                    href={resources.completeGuide}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => handlePdfClick("complete_guide")}
                    className="flex items-center justify-between p-4 bg-[#DE6E4B] text-white rounded-xl hover:bg-[#0A2342] transition-colors group"
                    data-testid="download-complete-guide"
                  >
                    <div className="flex items-center">
                      <FileText className="w-5 h-5 mr-3" />
                      <div>
                        <p className="font-semibold">Complete Guide</p>
                        <p className="text-xs opacity-80">Full sanctuary seeker's manual</p>
                      </div>
                    </div>
                    <Download className="w-5 h-5 group-hover:animate-bounce" />
                  </a>
                </div>
                
                <p className="text-center text-sm text-slate-500">
                  We also sent these to your email for easy access later!
                </p>
              </div>
            ) : (
              <>
                <h3 className="text-xl font-bold text-[#0A2342] mb-2 font-['Playfair_Display']">
                  Get Your Free Bundle
                </h3>
                <p className="text-slate-500 mb-6">4 printable guides sent to your inbox</p>
                
                <form onSubmit={handleSubmit} className="space-y-4">
                  <input
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="input-field"
                    placeholder="Enter your email"
                    data-testid="checklist-email"
                  />
                  <button
                    type="submit"
                    disabled={loading}
                    className="btn-primary w-full flex items-center justify-center"
                    data-testid="checklist-submit"
                  >
                    {loading ? (
                      <span className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
                    ) : (
                      <>Send Me the Bundle <ArrowRight className="w-5 h-5 ml-2" /></>
                    )}
                  </button>
                </form>

                <p className="text-xs text-slate-400 mt-4 text-center">
                  <Shield className="w-3 h-3 inline mr-1" />
                  Your privacy is respected. Unsubscribe anytime.
                </p>
              </>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Text Me Apartments Component
const TextMeApartments = ({ toast }) => {
  const [phone, setPhone] = useState("");
  const [area, setArea] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const areas = ["Houston", "Katy", "Sugar Land", "The Woodlands", "Conroe"];

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!phone || !area) return;
    
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, {
        name: "SMS Lead",
        email: `sms-${phone.replace(/\D/g, '')}@placeholder.com`,
        phone: phone,
        lead_type: "renter",
        source: "text_me_apartments",
        preferred_areas: [area],
        message: `SMS opt-in for ${area} apartments`
      });
      setSubmitted(true);
      toast({
        title: "You're on the list!",
        description: "We'll text you apartment options soon.",
      });
    } catch {
      toast({
        title: "Error",
        description: "Something went wrong. Please try again.",
        variant: "destructive"
      });
    }
    setLoading(false);
  };

  return (
    <section className="py-16 bg-[#8A9A5B]" id="text-me">
      <div className="container-main">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            className="bg-white rounded-2xl p-8 md:p-10 shadow-xl"
            data-testid="text-me-section"
          >
            <div className="grid md:grid-cols-2 gap-8 items-center">
              {/* Left Content */}
              <div>
                <span className="inline-flex items-center px-3 py-1 bg-[#DE6E4B]/10 text-[#DE6E4B] rounded-full text-sm font-medium mb-4">
                  <Smartphone className="w-4 h-4 mr-1" />
                  New Feature
                </span>
                <h2 className="text-2xl md:text-3xl font-bold text-[#0A2342] mb-4 font-['Playfair_Display']">
                  Text Me Apartments
                </h2>
                <p className="text-slate-600 mb-4">
                  Don't want to fill out forms? Just give us your number and preferred area — we'll text you apartment options directly.
                </p>
                <ul className="space-y-2 text-sm text-slate-500">
                  <li className="flex items-center">
                    <CheckCircle className="w-4 h-4 text-[#8A9A5B] mr-2" />
                    Get apartment matches via text
                  </li>
                  <li className="flex items-center">
                    <CheckCircle className="w-4 h-4 text-[#8A9A5B] mr-2" />
                    Quick responses, no spam
                  </li>
                  <li className="flex items-center">
                    <CheckCircle className="w-4 h-4 text-[#8A9A5B] mr-2" />
                    Text STOP anytime to opt out
                  </li>
                </ul>
              </div>

              {/* Right Form */}
              <div>
                {submitted ? (
                  <div className="text-center py-6">
                    <div className="w-14 h-14 bg-[#8A9A5B]/20 rounded-full flex items-center justify-center mx-auto mb-4">
                      <MessageCircle className="w-7 h-7 text-[#8A9A5B]" />
                    </div>
                    <h3 className="text-xl font-bold text-[#0A2342] mb-2">You're on the list!</h3>
                    <p className="text-slate-600 text-sm">We'll text you apartment options for {area} soon.</p>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                      <label className="text-sm font-medium text-slate-700 mb-2 block">Your Phone Number</label>
                      <div className="relative">
                        <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                        <input
                          type="tel"
                          required
                          value={phone}
                          onChange={(e) => setPhone(e.target.value)}
                          className="input-field pl-10"
                          placeholder="(555) 123-4567"
                          data-testid="text-me-phone"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-slate-700 mb-2 block">Preferred Area</label>
                      <select
                        required
                        value={area}
                        onChange={(e) => setArea(e.target.value)}
                        className="input-field"
                        data-testid="text-me-area"
                      >
                        <option value="">Select area...</option>
                        {areas.map((a) => (
                          <option key={a} value={a}>{a}</option>
                        ))}
                      </select>
                    </div>
                    <button
                      type="submit"
                      disabled={loading}
                      className="w-full bg-[#DE6E4B] hover:bg-[#0A2342] text-white font-semibold py-3 px-6 rounded-xl transition-all flex items-center justify-center"
                      data-testid="text-me-submit"
                    >
                      {loading ? (
                        <span className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
                      ) : (
                        <>
                          <MessageCircle className="w-5 h-5 mr-2" />
                          Text Me Apartments
                        </>
                      )}
                    </button>
                    <p className="text-xs text-slate-400 text-center">
                      By submitting, you agree to receive SMS. Msg & data rates may apply.
                    </p>
                  </form>
                )}
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// About Bryan Section
const AboutSection = () => {
  return (
    <section className="py-20 bg-white" id="about">
      <div className="container-main">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Image */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            className="relative"
          >
            <div className="aspect-[4/5] bg-[#0A2342]/10 rounded-2xl overflow-hidden">
              <img
                src="https://customer-assets.emergentagent.com/job_8045b904-4743-476b-a3b1-f22a821827d1/artifacts/565xb0in_Untitled%20image.jpg"
                alt="Bryan - Your Apartment Locator"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="absolute -bottom-6 -right-6 bg-[#DE6E4B] text-white p-4 rounded-xl shadow-lg">
              <p className="font-semibold">Licensed Apartment Locator</p>
              <p className="text-sm opacity-90">Houston, TX</p>
            </div>
          </motion.div>

          {/* Content */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
          >
            <span className="inline-flex items-center px-4 py-2 bg-[#8A9A5B]/20 text-[#8A9A5B] rounded-full text-sm font-medium mb-4">
              Meet Your Locator
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-[#0A2342] mb-6 font-['Playfair_Display']">
              Hi, I'm Bryan
            </h2>
            <p className="text-slate-600 text-lg mb-6">
              I help Houston renters find the right apartment — and avoid costly mistakes along the way. The checklist and tools on this site are the exact resources I give to all my clients.
            </p>
            <p className="text-slate-600 mb-8">
              Whether you're a first-time renter or relocating to Houston, having a systematic approach makes all the difference. I created <strong>Urban Nest Quest</strong> to help renters feel confident and prepared throughout their apartment search.
            </p>

            <div className="grid grid-cols-3 gap-6 mb-8">
              <div className="text-center" data-testid="about-expertise-claim">
                <div className="text-base font-bold text-[#DE6E4B] leading-snug">Houston Apartment &amp; Relocation Expertise</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-[#DE6E4B]">5</div>
                <div className="text-sm text-slate-500">Houston Areas</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-[#DE6E4B]">100%</div>
                <div className="text-sm text-slate-500">Free Service</div>
              </div>
            </div>

            <a
              href="/book"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary inline-flex items-center"
              data-testid="book-call-btn"
            >
              <Calendar className="w-5 h-5 mr-2" />
              Book a Free Consultation
            </a>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

const HomePage = () => {
  const navigate = useNavigate();
  const { toast } = useToast();

  const features = [
    {
      icon: Search,
      title: "Personalized Search",
      description: "We find apartments that match YOUR needs, not just what's available."
    },
    {
      icon: Sparkles,
      title: "Hidden Specials",
      description: "Access exclusive deals and move-in specials you won't find online."
    },
    {
      icon: MapPin,
      title: "Local Expertise",
      description: "Deep knowledge of Houston neighborhoods to find your perfect fit."
    },
    {
      icon: CheckCircle,
      title: "Free Service",
      description: "Our service is completely free for renters. Properties pay us."
    }
  ];

  const areas = ["Houston", "Katy", "Sugar Land", "The Woodlands", "Conroe"];

  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section className="relative min-h-[85vh] flex items-center bg-[#1F2A44]" data-testid="home-hero">
        <div
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage: "url('https://images.unsplash.com/photo-1622007151631-25aa98ab394b?auto=format&fit=crop&w=2000&q=80')",
            backgroundSize: "cover",
            backgroundPosition: "center"
          }}
        />

        <div className="container-main relative z-10 py-20">
          <div className="max-w-3xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <span className="inline-flex items-center px-4 py-1.5 rounded-full text-[11px] uppercase tracking-widest text-[#C8A96B] border border-[#C8A96B]/40 font-semibold mb-6">
                <MapPin className="w-3.5 h-3.5 mr-2" />
                Houston's Trusted Apartment Locator
              </span>
              <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 font-['Playfair_Display'] leading-tight">
                Find Your Perfect Houston Apartment
              </h1>
              <p className="text-lg md:text-xl text-white/80 mb-2 max-w-2xl mx-auto">
                Search Houston apartments, compare communities, schedule tours, and receive personalized apartment locating services at no cost to you.
              </p>
              <p className="text-[11px] uppercase tracking-widest text-[#C8A96B] font-semibold mb-10">
                Urban Nest Quest · {BRAND.brokerageLine}
              </p>
              <div className="flex flex-col items-center gap-4" data-testid="hero-cta-group">
                <div className="flex flex-col sm:flex-row items-stretch sm:items-start justify-center gap-3 sm:gap-4 w-full sm:w-auto">
                  <div className="flex flex-col items-center">
                    <Link
                      to="/apartment-search"
                      className="bg-[#C8A96B] hover:bg-[#B5915A] text-[#1F2A44] font-bold text-lg py-4 px-9 rounded-xl transition-colors duration-300 inline-flex items-center justify-center shadow-lg w-full sm:w-auto"
                      data-testid="hero-cta-start-search"
                    >
                      <Zap className="w-5 h-5 mr-2" />
                      Start My Apartment Search
                    </Link>
                    <span className="mt-1.5 text-xs text-white/70">Free personalized apartment matching</span>
                  </div>
                  <div className="flex flex-col items-center">
                    <a
                      href={TYPEFORM_QUIZ_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="border-2 border-white/60 hover:border-[#C8A96B] hover:text-[#C8A96B] text-white font-semibold py-3.5 px-7 rounded-xl transition-colors duration-300 inline-flex items-center justify-center w-full sm:w-auto"
                      data-testid="hero-cta-match-quiz"
                    >
                      <ClipboardList className="w-4 h-4 mr-2" />
                      Take the Match Quiz
                    </a>
                    <span className="mt-1.5 text-xs text-white/60">Not sure where to start?</span>
                  </div>
                </div>
                <a
                  href="#checklist"
                  className="text-sm text-white/70 hover:text-[#C8A96B] underline underline-offset-4 decoration-white/30 inline-flex items-center transition-colors py-2"
                  data-testid="hero-cta-checklist"
                >
                  <Download className="w-4 h-4 mr-1.5" />
                  Get the Free Renter's Toolkit
                </a>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 text-white/50 animate-bounce">
          <ChevronRight className="w-6 h-6 rotate-90" />
        </div>
      </section>

      {/* Popular Houston Apartment Searches — neighborhood landing pages */}
      <section className="py-16 sm:py-20 bg-[#FAFAF7]" data-testid="home-popular-neighborhoods">
        <div className="container-main">
          <div className="flex flex-col sm:flex-row items-start sm:items-end justify-between gap-4 mb-8">
            <div>
              <p className="text-[11px] uppercase tracking-widest text-[#C8A96B] font-bold">By neighborhood</p>
              <h2 className="mt-2 text-3xl sm:text-4xl font-bold text-[#1F2A44] font-['Playfair_Display']">
                Popular Houston Apartment Searches
              </h2>
              <p className="mt-2 text-[#333333]/75 max-w-2xl">
                Browse curated listings by neighborhood — each page includes live inventory, local context, and a free locator who knows the block.
              </p>
            </div>
            <Link to="/apartment-search"
                  className="inline-flex items-center gap-2 text-sm font-semibold text-[#1F2A44] hover:text-[#C8A96B] transition-colors"
                  data-testid="home-see-all-neighborhoods">
              See all Houston apartments <ChevronRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
            {HOMEPAGE_NEIGHBORHOODS.map((slug) => {
              const n = NEIGHBORHOODS[slug];
              if (!n) return null;
              return (
                <Link key={slug} to={`/apartments/${slug}`}
                      className="group block p-6 rounded-2xl bg-white border border-[#D9D9D9] hover:border-[#C8A96B] hover:shadow-lg transition-all"
                      data-testid={`home-hood-card-${slug}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="text-[10px] uppercase tracking-widest text-[#C8A96B] font-bold">{n.eyebrow}</p>
                      <h3 className="mt-1.5 text-xl font-bold text-[#1F2A44] font-['Playfair_Display'] group-hover:text-[#C8A96B] transition-colors">
                        {n.name}
                      </h3>
                    </div>
                    <span className="flex-shrink-0 w-9 h-9 rounded-full bg-[#1F2A44]/5 group-hover:bg-[#C8A96B] flex items-center justify-center transition-colors">
                      <ChevronRight className="w-4 h-4 text-[#1F2A44] group-hover:text-white transition-colors" />
                    </span>
                  </div>
                  <p className="mt-3 text-sm text-[#333333]/75 line-clamp-3">{n.intro}</p>
                  <div className="mt-4 flex flex-wrap gap-1.5">
                    {n.landmarks.slice(0, 3).map((l) => (
                      <span key={l} className="px-2 py-0.5 rounded-full bg-[#FAFAF7] text-[10px] text-[#333333]/70 border border-[#D9D9D9]">
                        {l}
                      </span>
                    ))}
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Moving to Houston Resources — featured articles */}
      <section className="py-16 sm:py-20 bg-white border-y border-[#D9D9D9]" data-testid="home-moving-resources">
        <div className="container-main">
          <div className="flex flex-col sm:flex-row items-start sm:items-end justify-between gap-4 mb-8">
            <div>
              <p className="text-[11px] uppercase tracking-widest text-[#C8A96B] font-bold">Resources</p>
              <h2 className="mt-2 text-3xl sm:text-4xl font-bold text-[#1F2A44] font-['Playfair_Display']">
                Moving to Houston Resources
              </h2>
              <p className="mt-2 text-[#333333]/75 max-w-2xl">
                Neighborhood guides and relocation playbooks from the locator team — no AI lorem, just specifics.
              </p>
            </div>
            <Link to="/blog"
                  className="inline-flex items-center gap-2 text-sm font-semibold text-[#1F2A44] hover:text-[#C8A96B] transition-colors"
                  data-testid="home-see-all-articles">
              Browse all articles <ChevronRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
            {latestCornerstoneArticles(6).map((a) => (
              <Link key={a.slug} to={`/blog/${a.slug}`}
                    className="group block p-6 rounded-2xl bg-[#FAFAF7] border border-[#D9D9D9] hover:border-[#C8A96B] hover:bg-white hover:shadow-md transition-all"
                    data-testid={`home-article-card-${a.slug}`}>
                <p className="text-[10px] uppercase tracking-widest text-[#C8A96B] font-bold">
                  Neighborhood Guide · {NEIGHBORHOODS[a.neighborhood]?.name}
                </p>
                <h3 className="mt-1.5 text-lg font-bold text-[#1F2A44] font-['Playfair_Display'] group-hover:text-[#C8A96B] transition-colors leading-snug">
                  {a.title}
                </h3>
                <p className="mt-2 text-xs text-[#333333]/70 line-clamp-3">{a.excerpt}</p>
                <span className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-[#C8A96B]">
                  Read article <ChevronRight className="w-3 h-3" />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Progressive Instant Search */}
      <ProgressiveSearch navigate={navigate} toast={toast} />

      {/* Features Section */}
      <section className="py-20 bg-white">
        <div className="container-main">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-[#0A2342] mb-4 font-['Playfair_Display']">
              Why Choose Urban Nest Quest?
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              We're not just another apartment listing site. We're your personal guide to finding the perfect Houston home.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="p-8 bg-[#E8E8E8]/50 rounded-2xl hover:bg-white hover:shadow-xl transition-all border border-transparent hover:border-slate-100"
                data-testid={`feature-${index}`}
              >
                <div className="w-14 h-14 bg-[#DE6E4B]/10 rounded-xl flex items-center justify-center mb-4">
                  <feature.icon className="w-7 h-7 text-[#DE6E4B]" />
                </div>
                <h3 className="text-xl font-semibold text-[#0A2342] mb-2">{feature.title}</h3>
                <p className="text-slate-600">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* About Bryan Section */}
      <AboutSection />

      {/* Checklist Lead Magnet */}
      <ChecklistSection toast={toast} />

      {/* Text Me Apartments */}
      <TextMeApartments toast={toast} />

      {/* Areas Section */}
      <section className="py-20 bg-white">
        <div className="container-main">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-[#0A2342] mb-4 font-['Playfair_Display']">
              Explore Houston Areas
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              From urban downtown living to suburban family neighborhoods, we've got you covered across the Houston metro.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 lg:grid-cols-5 gap-6">
            {areas.map((area, index) => (
              <motion.div
                key={area}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ delay: index * 0.1 }}
              >
                <Link
                  to={`/neighborhoods?area=${area}`}
                  className="block group"
                  data-testid={`area-card-${area.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <div className="aspect-[4/3] bg-[#0A2342] rounded-2xl relative overflow-hidden">
                    <div className="absolute inset-0 flex items-center justify-center">
                      <span className="text-white font-semibold text-xl font-['Playfair_Display']">{area}</span>
                    </div>
                    <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-[#0A2342] to-transparent">
                      <span className="text-[#DE6E4B] text-sm font-medium flex items-center">
                        Explore <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
                      </span>
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Deals Section */}
      <FeaturedDeals
        title="Featured Deals Near You"
        subtitle="Hand-picked apartments with the best value and move-in specials right now."
        limit={3}
        cta="apply"
        bg="bg-slate-50"
      />

      {/* Final CTA Section */}
      <section className="py-20 bg-[#0A2342]">
        <div className="container-main">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            className="text-center max-w-3xl mx-auto"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6 font-['Playfair_Display']">
              Ready to Find Your Perfect Houston Home?
            </h2>
            <p className="text-slate-300 text-lg mb-8">
              Stop wasting time on endless listings. Let me do the heavy lifting and match you with apartments that actually fit your needs.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <a
                href="#instant-search"
                className="bg-[#DE6E4B] hover:bg-white hover:text-[#0A2342] text-white font-semibold py-4 px-8 rounded-xl transition-all inline-flex items-center"
                data-testid="cta-start-search"
              >
                <Zap className="w-5 h-5 mr-2" />
                Start Your Search
              </a>
              <a
                href="/book"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-white/10 hover:bg-white hover:text-[#0A2342] text-white font-semibold py-4 px-8 rounded-xl transition-all border border-white/30 inline-flex items-center"
                data-testid="cta-book-call"
              >
                <Calendar className="w-5 h-5 mr-2" />
                Book Free Consultation
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
